import React, { useEffect, useState } from "react"
import Header from "../../components/Header"
import Footer from "../../components/Footer"
import SidebarFilterMarketplaceCategories from "../../components/sidebars/SidebarFilterMarketplaceCategories"
import Dropdown from "../../components/commons/Dropdown"
import CardItem from "../../components/cards/CardItem"
import DialogMarketplaceFilterCategories from "../../components/dialogs/DialogMarketplaceFilterCategories"
import { getAllListings, getAllListingsAndUpdateState, getListingsByBrands, getListingsByCategories, getListingsByConditions, getListingsByRarities } from "../../firebase/listings"
import { useLocation, useNavigate } from "react-router-dom"
import { getAllCategories } from "../../firebase/categories"
import { getShopifyProducts, mergeMarketplaceItems } from "../../firebase/shopify"
import { getBlockedSellerIds, isBlockedItem } from "../../firebase/blocks"
import { matchesCategoryFilter } from "../../utils/listingFilters"
import DialogConfirmation from "../../components/dialogs/DialogConfirmation";
import { useAtom } from "jotai"
import { priceRangeAtom, searchParamsAtom, userAtom, yearRangeAtom } from "../../store"
import { brands, conditions, rarities, boxTypes } from "../../utils/data"
import Button from "../../components/commons/Button"
import Pagination from '../../components/commons/Pagination'
import HeroSection from "../../sections/marketplace/HeroSection"
import Layout from "../../components/Layout"
import { Helmet } from "react-helmet-async"
import LoadingScreen from "../../components/Loading"
import DialogLoading from "../../components/dialogs/DialogLoading"

const MarketplaceCategories = () => {
	const [openDialog, setOpenDialog] = useState(null)
	const [listings, setListings] = useState([])
	const [userData, setUserData] = useAtom(userAtom)
	const [priceRange, setPriceRange] = useAtom(priceRangeAtom)
	const [yearRange,] = useAtom(yearRangeAtom)
	const [categories, setCategories] = useState([])
	const [selectedBrands, setSelectedBrands] = useState([])
	const [selectedConditions, setSelectedConditions] = useState([])
	const [searchInputText, setSearchText] = useState('')
	const [showSidebarFilter, setShowSidebarFilter] = useState(false)
	const [sortBy, setSortBy] = useState('price-low')
	const queries = new URLSearchParams(useLocation().search)
	const searchText = queries.get('search') ? queries.get('search') : ''
	const [loading, setLoading] = useState(true)
	const [itemsToShow, setItemsToShow] = useState(6)
	const [currentPage, setCurrentPage] = useState(1)
	const [searchParams, setSearchParams] = useAtom(searchParamsAtom)
	// null until loaded, for both of these: the grid stays behind the loading
	// dialog until the listings AND the buyer's block list are known, so a
	// blocked seller never gets a frame on screen.
	const [allListings, setAllListings] = useState(null)
	const [blockedSellerIds, setBlockedSellerIds] = useState(null)

	const navigate = useNavigate()

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });

		Promise.all([
			getAllListings(),
			getShopifyProducts()
		]).then(([localListings, shopifyProducts]) => {
			setAllListings(mergeMarketplaceItems(localListings, shopifyProducts))
		})
	}, [])

	// This page is public, so a signed-in buyer's `userData` only reaches it
	// after its first render -- the block list is resolved in its own effect
	// keyed to the buyer rather than fetched once alongside the listings.
	useEffect(() => {
		getBlockedSellerIds(userData?.id).then(setBlockedSellerIds)
	}, [userData?.id])

	// The grid waits for the buyer behind that stored session to arrive.
	// localStorage is what authContext hydrates from, and the only
	// synchronous signal that a buyer is coming: without it an empty block
	// list resolves first and the grid renders one frame of exactly the
	// sellers they asked never to see.
	const awaitingBuyer = Boolean(localStorage.getItem('userData')) && !userData?.id

	useEffect(() => {
		if (!allListings || !blockedSellerIds || awaitingBuyer) return

		let temp = allListings.reduce((acc, cur) => {
			if (listingInCategory(cur) && listingInRarity(cur) && listingInCondition(cur)) {
				acc.push(cur)
			}

			return acc
		}, [])

		setCurrentPage(1)

		updateListings(temp)
		setLoading(false)
		// setListings(temp)
	}, [allListings, blockedSellerIds, awaitingBuyer, searchParams, searchText, yearRange, priceRange])

	// A listing assigned a category but no sub-category used to fall through
	// this and disappear from the category it was filed under. The rule now
	// lives in utils/listingFilters, where it is unit tested.
	const listingInCategory = (listing) => matchesCategoryFilter(listing, searchParams.categories)

	const listingInRarity = (listing) => {
		if (!listing) return false
		if (searchParams.rarities.length === 0) return true
		if (searchParams.rarities.includes(listing.rarity)) return true
		return false
	}

	const listingInCondition = (listing) => {
		if (!listing) return false
		if (searchParams.conditions.length === 0) return true
		if (searchParams.conditions.includes(listing.condition)) return true
		return false
	}


	const onChangeSearch = (e) => {
		if (e.key === 'Enter') {
			navigate(`/marketplace/categories?search=${searchInputText}`)
		}
	}

	const getYear = (timestamp) => {
		let date
		if (timestamp) {
			date = new Date(timestamp.seconds * 1000)
		} else {
			date = new Date()
		}
		return date.getFullYear()
	}

	const updateListings = (items) => {
		let temp = [...items]
		temp = temp.filter(item => item.title.toLowerCase().includes(searchText.trim().toLowerCase())
			&& item.price >= priceRange.min
			&& item.price <= priceRange.max && item.released_year >= yearRange.min && item.released_year <= yearRange.max
			&& item.quantity > 0
			// One check covers both kinds of listing: a blocked account's own
			// listings and every Shopify store it owns.
			&& !isBlockedItem(item, blockedSellerIds)
		)


		// if (sortBy === 'price-low') {
		// 	temp.sort((a, b) => a.price - b.price)
		// }

		// if (sortBy === 'price-high') {
		// 	temp.sort((a, b) => b.price - a.price)
		// }

		setListings(temp)
	}

	const options = [
		{ value: "price-low", label: "Price Low" },
		{ value: "price-high", label: "Price High" },
	]

	const handleDropdownChange = (event) => {
		setSortBy(event.target.value)
	}

	const removeCategory = (categoryId) => {
		let newCategories = []
		if (selectedCategories.includes(categoryId)) {
			newCategories = selectedCategories.filter(id => id !== categoryId)
		} else {
			newCategories = [...selectedCategories, categoryId];
		}
		const categoryQuery = newCategories.join(',');

		navigate(`/marketplace/categories?categories=${categoryQuery}`)
	}

	const removeBrand = (brandValue) => {
		let newBrands = []
		if (selectedBrandValues.includes(brandValue)) {
			newBrands = selectedBrandValues.filter(id => id !== brandValue)
		} else {
			newBrands = [...selectedBrandValues, brandValue];
		}
		const brandQuery = newBrands.join(',');

		navigate(`/marketplace/categories?brands=${brandQuery}`)
	}

	const removeCondition = (conditionValue) => {
		let newConditions = []
		if (selectedConditionValues.includes(conditionValue)) {
			newConditions = selectedConditionValues.filter(id => id !== conditionValue)
		} else {
			newConditions = [...selectedConditionValues, conditionValue];
		}
		const conditionQuery = newConditions.join(',');

		navigate(`/marketplace/categories?conditions=${conditionQuery}`)
	}

	const containCategory = (categoryId) => {
		if (!searchParams.categories) return false
		if (searchParams.categories.findIndex(item => item.category === categoryId) > -1) return true
		return false
	}

	const toggleSubCategory = (categoryId, subCat) => {
		let temp = { ...searchParams }
		let newCategories = [...temp['categories']]

		newCategories = newCategories.reduce((acc, cur) => {
			if (cur.category === categoryId) {
				const newSubCategories = cur.subCategories.reduce((acc1, cur1) => {
					if (cur1 !== subCat) acc1.push(cur1)
					return acc1
				}, [])
				cur.subCategories = [...newSubCategories]
			}

			if (cur.subCategories.length > 0) {
				acc.push(cur)
			}

			return acc
		}, [])

		temp['categories'] = newCategories
		setSearchParams(temp)
	}

	const toggleRarity = (rarityId) => {
		let temp = { ...searchParams }
		const newRarities = [...temp.rarities]

		let index = newRarities.indexOf(rarityId)
		if (newRarities.includes(rarityId)) {
			newRarities.splice(index, 1)
		} else {
			newRarities.push(rarityId)
		}

		temp.rarities = [...newRarities]
		setSearchParams(temp)
	}

	const getRarity = (rarityId) => {
		return rarities.find(item => item.value === rarityId).label
	}

	const toggleBoxType = (boxTypeId) => {
		let temp = { ...searchParams }
		const newBoxTypes = [...temp.boxTypes]

		let index = newBoxTypes.indexOf(boxTypeId)
		if (newBoxTypes.includes(boxTypeId)) {
			newBoxTypes.splice(index, 1)
		} else {
			newBoxTypes.push(boxTypeId)
		}

		temp.boxTypes = [...newBoxTypes]
		setSearchParams(temp)
	}

	const getBoxType = (boxTypeId) => {
		return boxTypes.find(item => item.value === boxTypeId).label
	}

	const toggleCondition = (conditionValue) => {
		let temp = { ...searchParams }
		const newConditions = [...temp.conditions]

		let index = newConditions.indexOf(conditionValue)
		if (newConditions.includes(conditionValue)) {
			newConditions.splice(index, 1)
		} else {
			newConditions.push(conditionValue)
		}

		temp.conditions = [...newConditions]
		setSearchParams(temp)
	}

	const getCondition = (conditionId) => {
		return conditions.find(item => item.value === conditionId).label
	}

	return (
		<Layout>
			{
				loading &&
				<DialogLoading onClose={() => { }} />
			}

			<Helmet>
				<title>Find Rare & Pre-Owned Collectible Cards at the Best Prices</title>
				<meta name="description" content="Browse thousands of Pokémon, Naruto, Zelda, WWE, and Formula 1 cards. Buy rare TCG cards at the best prices. Secure, fast, and verified marketplace." />
			</Helmet>

			<DialogMarketplaceFilterCategories
				open={openDialog === "filter"}
				onClose={() => setOpenDialog(null)}
			/>

			{
				showSidebarFilter &&
				<div className="fixed top-0 left-0 z-[999] bg-[#1f1f1f] w-full lg:hidden">
					<div className="relative pt-20 max-h-[100vh] min-h-[100vh] px-5 mr-[1px] overflow-y-auto">
						<button className="absolute right-4 top-5" onClick={() => setShowSidebarFilter(false)}>
							<img src="/assets/icons/icon-close-white.svg" alt="close" />
						</button>

						<div className="text-white pb-32">
							<div className="rounded-[4px] relative border-[rgba(255,255,255,.4)] border-[1px] mb-8 group focus-within:border-[#1AB6F9] transition">
								<input
									className="px-4 py-4 bg-transparent w-full text-primary placeholder-primary text-sm"
									placeholder="SEARCH"
									onChange={(e) => setSearchText(e.currentTarget.value)}
									onKeyDown={onChangeSearch}
								>
								</input>

								<svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute top-[50%] -translate-y-1/2 right-4">
									<g clipPath="url(#clip0_772_1360)">
										<path d="M11.0109 19.2326C15.3457 19.2326 18.8598 15.7186 18.8598 11.3838C18.8598 7.04902 15.3457 3.53497 11.0109 3.53497C6.67615 3.53497 3.16211 7.04902 3.16211 11.3838C3.16211 15.7186 6.67615 19.2326 11.0109 19.2326Z" stroke="white" strokeWidth="2.09302" strokeLinecap="round" strokeLinejoin="round" />
										<path d="M16.5615 16.9337L21.9998 22.3719" stroke="white" strokeWidth="2.09302" strokeLinecap="round" strokeLinejoin="round" />
									</g>
									<defs>
										<clipPath id="clip0_772_1360">
											<rect width="25.1163" height="25.1163" fill="white" transform="translate(0.0224609 0.395386)" />
										</clipPath>
									</defs>
								</svg>
							</div>

							<SidebarFilterMarketplaceCategories show={showSidebarFilter} />

							<button className="mt-8 uppercase bg-primary text-white py-4 w-full rounded-[8px]" onClick={() => setShowSidebarFilter(false)}>
								Update
							</button>
						</div>
					</div>
				</div>
			}

			<HeroSection />

			<div className="container mx-auto lg:pt-14 lg:px-5 text-white relative max-lg:-mt-8">
				<div className="flex flex-col lg:flex-row lg:gap-16">
					<div className="w-full hidden lg:block lg:max-w-[285px] mt-9">
						<h4 className="flex items-center text-xl mb-8">
							<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2">
								<mask id="mask0_651_157" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="0" y="0" width="18" height="18">
									<rect width="18" height="18" fill="#D9D9D9" />
								</mask>
								<g mask="url(#mask0_651_157)">
									<path d="M8.25 15.75V11.25H9.75V12.75H15.75V14.25H9.75V15.75H8.25ZM2.25 14.25V12.75H6.75V14.25H2.25ZM5.25 11.25V9.75H2.25V8.25H5.25V6.75H6.75V11.25H5.25ZM8.25 9.75V8.25H15.75V9.75H8.25ZM11.25 6.75V2.25H12.75V3.75H15.75V5.25H12.75V6.75H11.25ZM2.25 5.25V3.75H9.75V5.25H2.25Z" fill="white" />
								</g>
							</svg>

							<div className="text-lg">Filter By</div>
						</h4>

						<hr className="gradient-border mb-6" />

						<SidebarFilterMarketplaceCategories />
					</div>


					<div className={`w-full block lg:hidden lg:max-w-[285px] ${showSidebarFilter ? 'mb-20' : ''}`}>
						<Button
							divClassName={`text-sm lg:text-base flex mb-6 mt-10 relative`}
							buttonClassName="justify-between"
							onClick={() => setShowSidebarFilter(!showSidebarFilter)}
						>
							<div className="flex">
								<img src="/assets/icons/icon-settings-white.svg" alt="dropdown" className="mr-1" />
								Filter by
							</div>

							<img src="/assets/icons/icon-arrow-right-empty-white.svg" alt="dropdown" />
						</Button>

						<hr className="border-b-[1px] border-[#333] mb-12 -mx-5"></hr>
					</div>

					<div className="flex lg:hidden mb-7 gap-5 flex-wrap">
						{
							searchParams.categories.map((cat, catIndex) =>
								cat.subCategories.map((subCat, subCatIndex) =>
									<div className="py-3 px-4 uppercase relative border-[1px] border-[#4C4C4C] text-white bg-[#0F0F0F] text-xs w-fit" key={`subcat-${catIndex}-${subCatIndex}`}>
										{subCat}

										<button className="absolute right-0 top-0 translate-x-1/2 -translate-y-1/2 bg-[#434343] p-1 rounded-[50%]"
											onClick={() => toggleSubCategory(cat.category, subCat)}>
											<img src="/assets/icons/icon-close-white.svg" alt="close" className="w-3" />
										</button>
									</div>
								)
							)
						}

						{
							searchParams.rarities.map((rarity, index) =>
								<div className="py-3 px-4 uppercase relative border-[1px] border-[#4C4C4C] text-white bg-[#0F0F0F] text-xs w-fit" key={`rarity-${index}`}>
									{getRarity(rarity)}

									<button className="absolute right-0 top-0 translate-x-1/2 -translate-y-1/2 bg-[#434343] p-1 rounded-[50%]"
										onClick={() => { toggleRarity(rarity) }}
									>
										<img src="/assets/icons/icon-close-white.svg" alt="close" className="w-3" />
									</button>
								</div>
							)
						}

						{
							searchParams.boxTypes.map((boxType, index) =>
								<div className="py-3 px-4 uppercase relative border-[1px] border-[#4C4C4C] text-white bg-[#0F0F0F] text-xs w-fit" key={`boxType-${index}`}>
									{getBoxType(boxType)}

									<button className="absolute right-0 top-0 translate-x-1/2 -translate-y-1/2 bg-[#434343] p-1 rounded-[50%]"
										onClick={() => { toggleBoxType(boxType) }}
									>
										<img src="/assets/icons/icon-close-white.svg" alt="close" className="w-3" />
									</button>
								</div>
							)
						}

						{
							searchParams.conditions.map((condition, index) =>
								<div className="py-3 px-4 uppercase relative border-[1px] border-[#4C4C4C] text-white bg-[#0F0F0F] text-xs w-fit" key={`condition-${index}`}>
									{getCondition(condition)}

									<button className="absolute right-0 top-0 translate-x-1/2 -translate-y-1/2 bg-[#434343] p-1 rounded-[50%]"
										onClick={() => { toggleCondition(condition) }}
									>
										<img src="/assets/icons/icon-close-white.svg" alt="close" className="w-3" />
									</button>
								</div>
							)
						}
					</div>

					<div className="w-full">
						{/* <div className="flex flex-col lg:flex-row items-center justify-between">
							{
								categories.length > 0 &&
								<div className="flex items-center gap-2 mt-5 lg:mt-0 lg:gap-4 overflow-x-auto w-full no-scrollbar">
									{
										categories.map((item) =>
											<button
												className="hover:bg-white relative w-full lg:w-fit hover:text-[#141414] justify-center text-sm lg:text-base flex items-center p-4 px-6 text-white border-style-decoration after:bottom-[-.5px] right-[-.5px] whitespace-nowrap"
												key={item.id}
												onClick={() => removeCategory(item.id)}
											>
												{item.name}
												<span className="cursor-pointer absolute top-2 right-2 hidden lg:block">
													<img src="/assets/icons/icon-x-blue.svg" />
												</span>
											</button>
										)}
								</div>
							}

							{
								selectedBrands.length > 0 &&
								<div className="flex items-center gap-2 mt-5 lg:mt-0 lg:gap-4 overflow-x-auto w-full no-scrollbar">
									{
										selectedBrands.map((item) =>
											<button
												className="hover:bg-white relative w-full lg:w-fit hover:text-[#141414] justify-center text-sm lg:text-base flex items-center p-4 px-6 text-white border-style-decoration after:bottom-[-.5px] right-[-.5px] whitespace-nowrap"
												key={item.value}
												onClick={() => removeBrand(item.value)}
											>
												{item.label}
												<span className="cursor-pointer absolute top-2 right-2 hidden lg:block">
													<img src="/assets/icons/icon-x-blue.svg" />
												</span>
											</button>
										)}
								</div>
							}

							<div className="flex items-center gap-2 mt-5 lg:mt-0 lg:gap-4 overflow-x-auto w-full no-scrollbar">
								{
									selectedConditions.length > 0 && selectedConditions.map((item) =>
										<button
											className="hover:bg-white relative w-full lg:w-fit hover:text-[#141414] justify-center text-sm lg:text-base flex items-center p-4 px-6 text-white border-style-decoration after:bottom-[-.5px] right-[-.5px] whitespace-nowrap"
											key={item.value}
											onClick={() => removeCondition(item.value)}
										>
											{item.label}
											<span className="cursor-pointer absolute top-2 right-2 hidden lg:block">
												<img src="/assets/icons/icon-x-blue.svg" />
											</span>
										</button>
									)}
							</div>


							<div className="flex items-center gap-3 w-full lg:max-w-[285px] mt-5 lg:mt-0">
								<button
									className="w-full lg:hidden hover:border-primary hover:text-white text-base flex justify-between items-center p-4 px-6 text-white border-style-decoration"
									onClick={() => setOpenDialog("filter")}
								>
									Filter By
									<img
										src="/assets/icons/icon-arrow-drop-down.svg"
										className={`rotate-180`}
									/>
								</button>
								<Dropdown
									options={options}
									placeholder="Popular"
									onChange={handleDropdownChange}
									className="w-full lg:max-w-[285px]"
								/>
							</div>
						</div> */}


						{
							listings.length > 0 ?
								<div className="grid grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6">
									{
										listings.slice(12 * (currentPage - 1), 12 * currentPage).map((item) =>
											<CardItem
												card={item}
												key={`listing-${item.id}`}
											/>
										)
									}
								</div> :
								<div className="mt-20">
									<h4 className="text-center">No Listings Here</h4>
								</div>
						}

						<div className="w-full lg:w-fit mx-auto mt-8 lg:mt-16">
							<Pagination itemsPerPage={12} totalItems={listings.length} onPageChange={(v) => setCurrentPage(v)} />
							{/* {
								itemsToShow < listings.length &&
								<button
									className="hover:bg-white w-full  lg:w-fit hover:text-[#141414] justify-center flex items-center p-4 px-6 text-white border-style-decoration after:bottom-[-.5px] right-[-.5px]"
									onClick={() => setItemsToShow(itemsToShow + 6)}
								>
									Load More
								</button>
							} */}
						</div>
					</div>
				</div>
			</div>
		</Layout >
	)
}

export default MarketplaceCategories
